/**
 * @fileoverview Schedule Date View - Handles UI interactions for the scheduled date picker
 *
 * @author Samantha Landen (slanden@yext.com)
 */
goog.module('yext.social.post.ScheduleDateView');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');
const Model = goog.require('yext.mvc.Model');
const {DEPRECATED_DatePicker} = goog.require('yext.ui.components.deprecated.DEPRECATED_DatePicker');

/** @constructor */
const ScheduleDateView = function(options) {
  this.options = options || {};

  /** @type {!jQuery} */
  this.$el = this.options.$el || $('.js-schedule-post-dialog');

  /** @type {string} */
  this.userTimezoneId_ = window.jstz.determine().name();

  /**
   * @type {Model}
   * @private
   */
  this.model_ = new Model().init({
    'timezone': '',
    'date': new Date(),
    'time': '',
    'error': false,
  });

  /**
   * @type {!Object<string, ?}
   * @private
   */
  this.initialState_ = null;

  /**
   * @type {Function}
   * @private
   */
  this.onApply_ = null;

  /**
   * @type {HTMLElement}
   * @private
   */
  this.$datePickerDomElement_ = goog.dom.getRequiredElementByClass('js-datepicker-container');
};

/**
 * @const
 * @type {string}
 */
ScheduleDateView.DEFAULT_MIN_TIME = '12:00am';

/**
 * @const
 * @type {string}
 */
ScheduleDateView.DEFAULT_MAX_TIME = '11:30pm';

/**
 * @const
 * @type {number}
 */
ScheduleDateView.MILLIS_IN_MINUTE = 60 * 1000;

/**
 * @const
 * @type {RegExp}
 */
ScheduleDateView.VALID_TIME_REGEX = /^(10|11|12|[1-9]):[0-5][0-9]\s?(a|p)m$/i;

/** @return this */
ScheduleDateView.prototype.init = function() {
  if (this.$el.length != 0) {
    this.bindUserInput_();
    this.selectUserTimezone_();
    this.initTimepicker_();
    this.bindModelChanges_();
    this.bindActions_();
    this.initialState_ = this.model_.raw();
  }

  return this;
};

/** @param {Function} cb */
ScheduleDateView.prototype.onApply = function(cb) {
  if (!yext.isFunction(cb)) {
    throw new TypeError('ScheduleDateView.onApply: Invalid cb, expected a function');
  }
  this.onApply_ = cb;
};

/** @return {!Object<string, ?>} */
ScheduleDateView.prototype.getData = function() {
  return {
    'scheduledDate': this.getDateTime_().getTime(),
    'userTimeZoneId': this.userTimezoneId_,
    'destinationTimeZoneId': this.model_.get('timezone'),
  };
};

ScheduleDateView.prototype.reset = function() {
  this.model_.resetState(this.initialState_);
  this.initDatepicker_();
};

ScheduleDateView.prototype.show = function($blah) {
  this.$el.slideDown().position({
    'my': 'right top',
    'at': 'left top',
    'offset': '-2 0',
    'of': $blah,
  });

  this.$el.find('.js-schedule-apply-btn').prop('disabled', false);

  // close the date view when clicking outside of it
  $(window).on('click.scheduledPost', function(e) {
    if (e.type == 'click'
        && $(e.target).closest('.js-schedule-post-dialog').length == 0
        && !$(e.target).hasClass('ui-timepicker-selected')
        && !$(e.target).hasClass('ui-icon-circle-triangle-e')
        && !$(e.target).hasClass('ui-icon-circle-triangle-w')) {
      this.hide_();
    }
  }.bind(this));
};

ScheduleDateView.prototype.getUserTimeZoneId = function() {
  return this.userTimezoneId_;
};

/** @private */
ScheduleDateView.prototype.bindUserInput_ = function() {
  this.$el.find('select.js-timezone-selector').inputchange(function(e) {
    e.stopPropagation();
    var $selected = $(e.target);
    if ($selected.is('select')) {
      $selected = $selected.find(':selected');
    }
    this.model_.set({
      'timezone': $selected.val(),
      'offset': $selected.data('offset'),
    });
  }.bind(this));

  var $timepicker = this.$el.find('.js-timepicker');
  $timepicker.inputchange(function(e) {
    var time = $timepicker.val();
    if (!ScheduleDateView.VALID_TIME_REGEX.test(time)) {
      this.model_.set('error', true);
    } else {
      this.model_.set('time', $timepicker.timepicker('getTime'));
    }
  }.bind(this));

  $timepicker.on('changeTime', function(e) {
    this.model_.set('time', $timepicker.timepicker('getTime'));
  }.bind(this));

  this.initDatepicker_();
};

/** @private */
ScheduleDateView.prototype.selectUserTimezone_ = function() {
  var $timezoneSelect = this.$el.find('select.js-timezone-selector');

  if ($timezoneSelect.find('option[value="' + this.userTimezoneId_ + '"]').length == 0) {
    var timezones = this.options.timezones.filter(function(tz) {
      return tz['id'] == this.userTimezoneId_;
    }.bind(this));

    if (timezones.length == 0) {
      if (this.userTimezoneId_ == 'UTC') {
        timezones.push({
          'id': this.userTimezoneId_,
          'displayName': this.userTimezoneId_,
          'millis': 0,
        });
      } else {
        throw new TypeError('ScheduleDateView: Unsupported timezone: ' + this.userTimezoneId_);
      }
    }

    var tz = timezones[0];
    $timezoneSelect.append($('<option>', {
      'value': this.userTimezoneId_,
      'text': tz['displayName'],
      'data': {
        'offset': tz['millis'],
      },
    }));
  }

  $timezoneSelect.val(this.userTimezoneId_).trigger('change');
};

ScheduleDateView.prototype.initDatepicker_ = function() {
  ReactDOM.unmountComponentAtNode(this.$datePickerDomElement_);

  const now = new Date();
  // Date::getMonth is zero indexed in JS, e.g. january=0, february=1, etc.
  const dateString = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  const datePickerProps = {
    onChange: date => {
      const localDate = new Date(date);
      // getTimezoneOffset returns offset in minutes: multiply by 60000 to get milliseconds
      // and add to milliseconds of local date to offset timezone
      // necessary to avoid the timezone pushing us backward a date from the one selected
      this.model_.set(
        'date',
        new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000));
    },
    min: dateString,
    date: dateString,
  };
  ReactDOM.render(
    React.createElement(DEPRECATED_DatePicker, datePickerProps, null),
    this.$datePickerDomElement_,
  );
};

/** @private */
ScheduleDateView.prototype.initTimepicker_ = function() {
  var $timepicker = this.$el.find('.js-timepicker');
  $timepicker.timepicker({
    timeFormat: 'hm',
    scrollDefault: true,
    minTime: ScheduleDateView.DEFAULT_MIN_TIME,
    maxTime: ScheduleDateView.DEFAULT_MAX_TIME,
    forceRoundTime: false,
  });

  var startTime = new Date();
  startTime.setMinutes(startTime.getMinutes() + 30);
  startTime.setMinutes(startTime.getMinutes() - 30 < 0 ? 0 : 30);
  $timepicker.timepicker('setTime', startTime);
  $timepicker.trigger('changeTime');
};

/**
 * @private
 * @suppress {deprecated}
 */
ScheduleDateView.prototype.bindModelChanges_ = function() {
  this.model_.on('change:timezone,change:time,change:date', function() {
    this.validateFutureDate_();
  }.bind(this));

  this.model_.on('change:error', function() {
    this.render_();
  }.bind(this));

  this.model_.on('reset', function() {
    this.initDatepicker_();
    this.$el.find('.js-timepicker').timepicker('setTime', this.model_.get('time'));
    this.$el.find('.js-timezone-selector').val(this.model_.get('timezone')).trigger('change');
    this.render_();
  }.bind(this));
};

/**
 * Makes sure the selected time is in the future, if it isn't it disables the 'Schedule' button and
 * shows a warning. This method was largely copied from js/yext/social/scheduledpostdialog.js
 * @private
 */
ScheduleDateView.prototype.validateFutureDate_ = function() {
  // since there's no way to convert between timezones without a plugin
  // and we can't currently use a timezone plugin atm we're going to have
  // to fake it

  // today is going to be in the browsers local time so we are going to get the
  // local browsers offset and subtract that from the user's selected timezone's
  // offset to make sure the date is ACTUALLY in the future

  // So GMT-4 returns .getTimezoneOffset() = +240 (minutes) while we get -240
  // from the select box, we're just going to manually offset that here
  var today = new Date();
  var dateSelected = this.getDateTime_();
  var todayAsMilliseconds = today.getTime();
  var updatedDateAsMilliseconds = dateSelected.getTime();
  var localOffset = -(today.getTimezoneOffset() *
                    ScheduleDateView.MILLIS_IN_MINUTE);
  var dateSelectOffset = parseInt(this.model_.get('offset'));
  var timeDifference = localOffset - dateSelectOffset;

  // check to see if we even need to do anything to adjust the times
  if (timeDifference != 0) {
    updatedDateAsMilliseconds += timeDifference;
  }

  // update the error if the user's selected datetime is in the future
  this.model_.set('error', updatedDateAsMilliseconds < todayAsMilliseconds);
};

/**
 * @private
 * @return {Date}
 */
ScheduleDateView.prototype.getDateTime_ = function() {
  var date = this.model_.get('date');
  var time = this.model_.get('time');

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds());
};

/** @private */
ScheduleDateView.prototype.render_ = function() {
  this.$el.find('.js-schedule-apply-btn').prop('disabled', this.model_.get('error'));
  this.$el.find('.js-future-error-block').toggle(this.model_.get('error'));
};

/** @private */
ScheduleDateView.prototype.bindActions_ = function() {
  this.$el.find('.js-schedule-apply-btn').on('click', function(e) {
    if (this.onApply_) {
      this.onApply_(this.getData());
      $(e.currentTarget).prop('disabled', true);
    }
  }.bind(this));

  this.$el.find('.js-schedule-cancel-btn').on('click', function() {
    this.hide_();
    this.reset();
  }.bind(this));
};

/** @private */
ScheduleDateView.prototype.hide_ = function() {
  this.$el.slideUp();
  $(window).off('click.scheduledPost');
};

exports = ScheduleDateView;
